import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import {
  fetchDbBackupToolsStatus,
  fetchDbCollections,
  fetchDbBackups,
  createDbBackup,
  deleteDbBackup,
  restoreDbBackup,
  downloadDbBackup,
  previewDbBackupUpload,
  confirmDbBackupUpload,
} from '../services/dbBackupService';

const RESTORE_CONFIRM_TEXT = 'YUKLE';

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  if (error?.status === 403) return 'Bu işlem için yetkiniz yok.';
  return (
    error?.message ||
    error?.data?.mesaj ||
    error?.data?.message ||
    fallback
  );
};

const formatFileSize = (bytes) => {
  if (bytes == null || bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const formatDate = (value) => {
  if (!value) return '—';
  if (typeof value === 'object' && !(value instanceof Date)) {
    if (Object.keys(value).length === 0) return '—';
  }
  try {
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const DbBackupPage = () => {
  const { confirm, promptText } = useConfirmDialog();
  const uploadInputRef = useRef(null);

  const [toolsStatus, setToolsStatus] = useState(null);
  const [collections, setCollections] = useState([]);
  const [selectedCollections, setSelectedCollections] = useState(new Set());
  const [backupLabel, setBackupLabel] = useState('');
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [actionId, setActionId] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [restoringUpload, setRestoringUpload] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [toolsRes, colRes, backupRes] = await Promise.all([
        fetchDbBackupToolsStatus(),
        fetchDbCollections(),
        fetchDbBackups(),
      ]);
      setToolsStatus(toolsRes?.veri || null);
      setCollections(colRes?.veri?.collections || []);
      setBackups(backupRes?.veri?.yedekler || []);
    } catch (err) {
      setError(getErrorMessage(err, 'Veriler yüklenemedi'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const allSelected = useMemo(
    () => collections.length > 0 && selectedCollections.size === collections.length,
    [collections.length, selectedCollections.size]
  );

  const toggleCollection = (name) => {
    setSelectedCollections((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const handleSelectAll = () => {
    setSelectedCollections(new Set(collections));
  };

  const handleClearSelection = () => {
    setSelectedCollections(new Set());
  };

  const handleCreateBackup = async () => {
    if (selectedCollections.size === 0) {
      window.showNotification?.('error', 'En az bir koleksiyon seçin');
      return;
    }
    if (!toolsStatus?.ready) {
      window.showNotification?.('error', 'Sunucuda mongodump/mongorestore araçları hazır değil');
      return;
    }

    setCreating(true);
    try {
      await createDbBackup({
        collections: Array.from(selectedCollections),
        label: backupLabel.trim() || undefined,
      });
      window.showNotification?.('success', 'Yedek oluşturuldu');
      setBackupLabel('');
      await loadAll();
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Yedek oluşturulamadı'));
    } finally {
      setCreating(false);
    }
  };

  const askRestoreConfirm = async (description) => {
    const ok = await confirm({
      title: 'Yedek yükleme',
      message: `${description}\n\nDevam etmek için onay kutusuna geçin.`,
      variant: 'danger',
      confirmLabel: 'Devam',
    });
    if (!ok) return null;

    const typed = await promptText({
      title: 'Onay gerekli',
      message: `Bu işlem geri alınamaz. Onaylamak için "${RESTORE_CONFIRM_TEXT}" yazın.`,
      placeholder: RESTORE_CONFIRM_TEXT,
      minLength: RESTORE_CONFIRM_TEXT.length,
      multiline: false,
      confirmLabel: 'Yükle',
    });
    if (typed !== RESTORE_CONFIRM_TEXT) return null;
    return RESTORE_CONFIRM_TEXT;
  };

  const handleRestore = async (backup) => {
    const desc = backup.fullBackup
      ? 'Tüm veritabanı silinip yedekten yüklenecek.'
      : `Yalnızca şu koleksiyonlar silinip yüklenecek: ${backup.collections.join(', ')}`;

    const confirmText = await askRestoreConfirm(desc);
    if (!confirmText) return;

    setActionId(backup.id);
    try {
      await restoreDbBackup(backup.id, confirmText);
      window.showNotification?.('success', 'Yedek yüklendi');
      await loadAll();
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Yedek yüklenemedi'));
    } finally {
      setActionId(null);
    }
  };

  const handleDownload = async (backup) => {
    setActionId(backup.id);
    try {
      const name = backup.label
        ? `${backup.label.replace(/[^\w\-]+/g, '_')}.zip`
        : backup.storedFilename || 'backup.zip';
      await downloadDbBackup(backup.id, name);
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Yedek indirilemedi'));
    } finally {
      setActionId(null);
    }
  };

  const handleDelete = async (backup) => {
    const ok = await confirm({
      title: 'Yedeği sil',
      message: 'Bu yedek kalıcı olarak silinsin mi?',
      variant: 'danger',
      confirmLabel: 'Sil',
    });
    if (!ok) return;

    setActionId(backup.id);
    try {
      await deleteDbBackup(backup.id);
      window.showNotification?.('success', 'Yedek silindi');
      await loadAll();
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Yedek silinemedi'));
    } finally {
      setActionId(null);
    }
  };

  const handleUploadSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.zip')) {
      window.showNotification?.('error', 'Yalnızca .zip dosyası seçilebilir');
      return;
    }

    setUploading(true);
    setUploadPreview(null);
    try {
      const res = await previewDbBackupUpload(file);
      setUploadPreview(res?.veri || null);
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Yedek önizlenemedi'));
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmUploadRestore = async () => {
    if (!uploadPreview?.uploadToken) return;

    const manifest = uploadPreview.manifest || {};
    const desc = manifest.fullBackup
      ? 'Tüm veritabanı silinip yüklenen yedekten restore edilecek.'
      : `Yalnızca şu koleksiyonlar silinip yüklenecek: ${(manifest.collections || []).join(', ')}`;

    const confirmText = await askRestoreConfirm(desc);
    if (!confirmText) return;

    setRestoringUpload(true);
    try {
      await confirmDbBackupUpload({
        uploadToken: uploadPreview.uploadToken,
        confirmText,
      });
      window.showNotification?.('success', 'Yedek yüklendi');
      setUploadPreview(null);
      await loadAll();
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Yedek yüklenemedi'));
    } finally {
      setRestoringUpload(false);
    }
  };

  const creatorName = (backup) => {
    const u = backup.createdBy;
    if (!u) return '—';
    const full = `${u.ad || ''} ${u.soyad || ''}`.trim();
    return full || u.kullaniciAdi || '—';
  };

  return (
    <div className="page-container db-backup-page">
      <div className="page-header">
        <h1>DB Yedekleme</h1>
      </div>

      {toolsStatus && !toolsStatus.ready && (
        <div
          style={{
            padding: '12px 16px',
            backgroundColor: '#fef3c7',
            color: '#92400e',
            borderRadius: '8px',
            marginBottom: '16px',
            border: '1px solid #fcd34d',
          }}
        >
          <strong>Araç uyarısı:</strong> Sunucuda mongodump veya mongorestore eksik. Yedek alma/yükleme çalışmaz.
        </div>
      )}

      {error && (
        <div
          style={{
            padding: '12px 16px',
            backgroundColor: '#fee2e2',
            color: '#991b1b',
            borderRadius: '8px',
            marginBottom: '16px',
          }}
        >
          {error}
        </div>
      )}

      {/* Yedek Al */}
      <section
        style={{
          background: '#fff',
          border: '1px solid var(--border-color, #e2e8f0)',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '24px',
        }}
      >
        <h2 style={{ fontSize: '18px', marginBottom: '8px' }}>Yedek Al</h2>
        <p style={{ color: 'var(--text-gray)', fontSize: '14px', marginBottom: '16px' }}>
          Yedeklemek istediğiniz koleksiyonları seçin. Tümünü seçerseniz tam yedek oluşturulur.
        </p>

        <div style={{ marginBottom: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button type="button" className="secondary-btn" onClick={handleSelectAll} disabled={loading}>
            Tümünü Seç
          </button>
          <button type="button" className="secondary-btn" onClick={handleClearSelection} disabled={loading}>
            Temizle
          </button>
          <span style={{ alignSelf: 'center', fontSize: '14px', color: 'var(--text-gray)' }}>
            {selectedCollections.size} / {collections.length} seçili
            {allSelected && collections.length > 0 ? ' (Tam yedek)' : ''}
          </span>
        </div>

        {loading ? (
          <div style={{ padding: '20px', color: '#64748b' }}>Koleksiyonlar yükleniyor...</div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '8px',
              maxHeight: '280px',
              overflowY: 'auto',
              padding: '12px',
              border: '1px solid var(--border-color, #e2e8f0)',
              borderRadius: '8px',
              marginBottom: '16px',
            }}
          >
            {collections.map((name) => (
              <label
                key={name}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={selectedCollections.has(name)}
                  onChange={() => toggleCollection(name)}
                />
                <span>{name}</span>
              </label>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Opsiyonel etiket (örn. canlı öncesi)"
            value={backupLabel}
            onChange={(e) => setBackupLabel(e.target.value)}
            maxLength={200}
            style={{
              flex: 1,
              minWidth: '220px',
              padding: '10px 12px',
              border: '1px solid var(--border-color, #e2e8f0)',
              borderRadius: '8px',
            }}
          />
          <button
            type="button"
            className="primary-btn"
            onClick={handleCreateBackup}
            disabled={creating || loading || selectedCollections.size === 0}
          >
            {creating ? (
              <>
                <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }} />
                Oluşturuluyor...
              </>
            ) : (
              <>
                <i className="fas fa-database" style={{ marginRight: '8px' }} />
                Yedek Oluştur
              </>
            )}
          </button>
        </div>
      </section>

      {/* Yedekler listesi */}
      <section
        style={{
          background: '#fff',
          border: '1px solid var(--border-color, #e2e8f0)',
          borderRadius: '8px',
          overflow: 'hidden',
          marginBottom: '24px',
        }}
      >
        <div style={{ padding: '20px 20px 0' }}>
          <h2 style={{ fontSize: '18px', marginBottom: '8px' }}>Kayıtlı Yedekler</h2>
        </div>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Yükleniyor...</div>
        ) : backups.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Henüz yedek yok</div>
        ) : (
          <div className="table-container">
            <table className="admins-table">
              <thead>
                <tr>
                  <th>Tarih</th>
                  <th>Etiket</th>
                  <th>Tür</th>
                  <th>Koleksiyon</th>
                  <th>Boyut</th>
                  <th>Oluşturan</th>
                  <th className="islemler-sutunu">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {backups.map((backup) => (
                  <tr key={backup.id}>
                    <td>{formatDate(backup.createdAt || backup.updatedAt)}</td>
                    <td>{backup.label || '—'}</td>
                    <td>
                      <span
                        style={{
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 500,
                          background: backup.fullBackup ? '#dbeafe' : '#f1f5f9',
                          color: backup.fullBackup ? '#1d4ed8' : '#475569',
                        }}
                      >
                        {backup.fullBackup ? 'Tam' : 'Kısmi'}
                      </span>
                    </td>
                    <td>{backup.collections?.length || 0}</td>
                    <td>{formatFileSize(backup.size)}</td>
                    <td>{creatorName(backup)}</td>
                    <td className="table-actions islemler-sutunu">
                      <button
                        type="button"
                        className="category-action-btn"
                        title="İndir"
                        disabled={actionId === backup.id}
                        onClick={() => handleDownload(backup)}
                      >
                        <i className="fas fa-download" />
                      </button>
                      <button
                        type="button"
                        className="category-action-btn edit-btn"
                        title="Yükle"
                        disabled={actionId === backup.id}
                        onClick={() => handleRestore(backup)}
                      >
                        <i className="fas fa-upload" />
                      </button>
                      <button
                        type="button"
                        className="category-action-btn delete-btn"
                        title="Sil"
                        disabled={actionId === backup.id}
                        onClick={() => handleDelete(backup)}
                      >
                        <i className="fas fa-trash" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Dışarıdan yükle */}
      <section
        style={{
          background: '#fff',
          border: '1px solid var(--border-color, #e2e8f0)',
          borderRadius: '8px',
          padding: '20px',
        }}
      >
        <h2 style={{ fontSize: '18px', marginBottom: '8px' }}>Dışarıdan Yedek Yükle</h2>
        <p style={{ color: 'var(--text-gray)', fontSize: '14px', marginBottom: '16px' }}>
          Bu sistemle oluşturulmuş manifest.json içeren .zip dosyasını yükleyin.
        </p>

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
          <button
            type="button"
            className="secondary-btn"
            onClick={() => uploadInputRef.current?.click()}
            disabled={uploading || restoringUpload}
          >
            {uploading ? (
              <>
                <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }} />
                Önizleniyor...
              </>
            ) : (
              <>
                <i className="fas fa-file-archive" style={{ marginRight: '8px' }} />
                ZIP Seç
              </>
            )}
          </button>
          <input
            ref={uploadInputRef}
            type="file"
            accept=".zip,application/zip"
            style={{ display: 'none' }}
            onChange={handleUploadSelect}
          />
        </div>

        {uploadPreview?.manifest && (
          <div
            style={{
              padding: '16px',
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
            }}
          >
            <div style={{ marginBottom: '8px', fontWeight: 600 }}>Önizleme</div>
            <div style={{ fontSize: '14px', color: '#475569', marginBottom: '4px' }}>
              Veritabanı: {uploadPreview.manifest.dbName}
            </div>
            <div style={{ fontSize: '14px', color: '#475569', marginBottom: '4px' }}>
              Tür: {uploadPreview.manifest.fullBackup ? 'Tam yedek' : 'Kısmi yedek'}
            </div>
            <div style={{ fontSize: '14px', color: '#475569', marginBottom: '12px' }}>
              Koleksiyonlar: {(uploadPreview.manifest.collections || []).join(', ')}
            </div>
            <button
              type="button"
              className="danger-btn"
              onClick={handleConfirmUploadRestore}
              disabled={restoringUpload}
            >
              {restoringUpload ? (
                <>
                  <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }} />
                  Yükleniyor...
                </>
              ) : (
                'Bu Yedeği Yükle'
              )}
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default DbBackupPage;
